import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Key,
  Add,
  Delete,
  Check,
  Warning,
  People,
  Business,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface APIKey {
  id: string;
  provider: string;
  key_name: string;
  is_active: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
}

const OrganizationSettings: React.FC = () => {
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add API Key Dialog
  const [addKeyDialog, setAddKeyDialog] = useState(false);
  const [newKeyProvider, setNewKeyProvider] = useState<'anthropic' | 'gemini'>('anthropic');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [newKeyName, setNewKeyName] = useState('');
  const [addingKey, setAddingKey] = useState(false);

  // Fetch API keys
  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/organization/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApiKeys(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKey = async () => {
    if (!newKeyValue.trim() || !newKeyName.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setAddingKey(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/organization/api-keys`,
        {
          provider: newKeyProvider,
          api_key: newKeyValue,
          key_name: newKeyName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess(`${newKeyProvider === 'anthropic' ? 'Anthropic' : 'Gemini'} API key added successfully`);
      setAddKeyDialog(false);
      setNewKeyValue('');
      setNewKeyName('');
      fetchAPIKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add API key');
    } finally {
      setAddingKey(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to delete this API key? This will prevent AI operations until a new key is added.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/organization/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('API key deleted successfully');
      fetchAPIKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete API key');
    }
  };

  const handleTestKey = async (provider: string) => {
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/organization/api-keys/test/${provider}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === 'success') {
        setSuccess(`${provider} API key is valid: ${response.data.message}`);
      } else {
        setError(`${provider} API key test failed: ${response.data.message}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to test API key');
    }
  };

  const getKeyByProvider = (provider: string) => {
    return apiKeys.find((key) => key.provider === provider && key.is_active);
  };

  const anthropicKey = getKeyByProvider('anthropic');
  const geminiKey = getKeyByProvider('gemini');

  // Check if user is admin (for delete/test actions)
  const isAdmin = user?.role === 'admin' || user?.role === 'superuser';

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/projects')}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Organization Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {organization?.name}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab icon={<Key />} label="API Keys" iconPosition="start" />
          <Tab icon={<People />} label="Users" iconPosition="start" />
          <Tab icon={<Business />} label="General" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* API Keys Tab */}
      {tabValue === 0 && (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              AI Provider API Keys
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddKeyDialog(true)}
            >
              Add API Key
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            API keys are encrypted and stored securely. They're used for AI-powered requirements extraction and compliance evaluation.
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Anthropic Key Card */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Anthropic (Claude)
                      </Typography>
                      {anthropicKey ? (
                        <>
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                            icon={<Check />}
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Key: {anthropicKey.key_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Usage: {anthropicKey.usage_count} calls
                          </Typography>
                          {anthropicKey.last_used_at && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Last used: {new Date(anthropicKey.last_used_at).toLocaleString()}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          <Chip
                            label="Not Configured"
                            color="warning"
                            size="small"
                            icon={<Warning />}
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Requirements extraction will not work without this key.
                          </Typography>
                        </>
                      )}
                    </Box>
                    {anthropicKey && (
                      <Box>
                        <Button
                          size="small"
                          onClick={() => handleTestKey('anthropic')}
                          sx={{ mr: 1 }}
                        >
                          Test
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteKey(anthropicKey.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Gemini Key Card */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Google Gemini
                      </Typography>
                      {geminiKey ? (
                        <>
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                            icon={<Check />}
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Key: {geminiKey.key_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Usage: {geminiKey.usage_count} calls
                          </Typography>
                          {geminiKey.last_used_at && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Last used: {new Date(geminiKey.last_used_at).toLocaleString()}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          <Chip
                            label="Not Configured"
                            color="warning"
                            size="small"
                            icon={<Warning />}
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Compliance evaluation will not work without this key.
                          </Typography>
                        </>
                      )}
                    </Box>
                    {geminiKey && (
                      <Box>
                        <Button
                          size="small"
                          onClick={() => handleTestKey('gemini')}
                          sx={{ mr: 1 }}
                        >
                          Test
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteKey(geminiKey.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Help Text */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Where to get API keys:
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>Anthropic (Claude):</strong> <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">console.anthropic.com</a>
                  <br />
                  • <strong>Google Gemini:</strong> <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">aistudio.google.com/app/apikey</a>
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      )}

      {/* Users Tab */}
      {tabValue === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Organization Users
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            User management UI coming soon. For now, users can join using invite code: <strong>{organization?.invite_code}</strong>
          </Alert>
        </Box>
      )}

      {/* General Tab */}
      {tabValue === 2 && (
        <Box>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Organization Information
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Organization Name"
                    secondary={organization?.name}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Invite Code"
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <code style={{ fontSize: '1.1em', fontWeight: 600 }}>
                          {organization?.invite_code}
                        </code>
                        <Button
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(organization?.invite_code || '');
                            setSuccess('Invite code copied to clipboard');
                          }}
                        >
                          Copy
                        </Button>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Subscription Tier"
                    secondary={organization?.subscription_tier || 'Free'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Add API Key Dialog */}
      <Dialog open={addKeyDialog} onClose={() => !addingKey && setAddKeyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add API Key</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Provider
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant={newKeyProvider === 'anthropic' ? 'contained' : 'outlined'}
                onClick={() => setNewKeyProvider('anthropic')}
                fullWidth
              >
                Anthropic (Claude)
              </Button>
              <Button
                variant={newKeyProvider === 'gemini' ? 'contained' : 'outlined'}
                onClick={() => setNewKeyProvider('gemini')}
                fullWidth
              >
                Google Gemini
              </Button>
            </Box>

            <TextField
              fullWidth
              label="Key Name"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder={`e.g., "Production ${newKeyProvider === 'anthropic' ? 'Claude' : 'Gemini'} Key"`}
              margin="normal"
              helperText="A friendly name to identify this key"
            />

            <TextField
              fullWidth
              label="API Key"
              type="password"
              value={newKeyValue}
              onChange={(e) => setNewKeyValue(e.target.value)}
              placeholder={newKeyProvider === 'anthropic' ? 'sk-ant-api03-...' : 'AIza...'}
              margin="normal"
              helperText={
                newKeyProvider === 'anthropic'
                  ? 'Get from console.anthropic.com'
                  : 'Get from aistudio.google.com/app/apikey'
              }
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Your API key will be encrypted before storage. It will never be displayed again.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddKeyDialog(false)} disabled={addingKey}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddKey}
            disabled={addingKey}
          >
            {addingKey ? <CircularProgress size={20} /> : 'Add Key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationSettings;
